import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Plus, ShieldAlert } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  garage_code: "", name: "", business_name: "", phone: "", email: "",
  gstin: "", state_name: "", state_code: "", address: "",
  admin_username: "", admin_password: "", admin_full_name: "", admin_email: "",
};

export const PlatformGaragesSection = () => {
  const { user } = useAuth();
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchGarages = async () => {
    try {
      const res = await axios.get(`${API}/platform/garages`);
      setGarages(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch garages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGarages(); }, []);

  if (!user?.is_platform_owner) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="platform-garages-restricted">
        <Card className="p-8 text-center">
          <ShieldAlert className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Platform Owner Only</h3>
          <p className="text-gray-600">Only the platform owner can create or manage garages.</p>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/platform/garages`, {
        ...form,
        garage_code: form.garage_code.toUpperCase().trim(),
      });
      toast.success(`Garage "${form.name}" created! Login code: ${form.garage_code.toUpperCase().trim()}`);
      setShowAddDialog(false);
      setForm(emptyForm);
      fetchGarages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create garage");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-garages-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6" /> Garages
          </h2>
          <p className="text-gray-600 mt-1">Every garage (tenant) on the platform, and their own isolated data.</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="create-garage-button">
          <Plus className="h-4 w-4 mr-2" /> Create Garage
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : garages.length === 0 ? (
            <p className="text-gray-500">No garages yet.</p>
          ) : (
            <div className="space-y-3">
              {garages.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center justify-between border rounded-lg p-4"
                  data-testid={`garage-row-${g.garage_code}`}
                >
                  <div>
                    <p className="font-semibold text-gray-900">{g.name}</p>
                    <p className="text-sm text-gray-500">{g.business_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={g.is_active ? "default" : "secondary"}>
                      {g.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline" className="font-mono" data-testid={`garage-code-${g.garage_code}`}>
                      Code: {g.garage_code}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Garage</DialogTitle>
            <DialogDescription>
              Sets up a brand-new, fully isolated garage with its own login. Share the garage code + admin
              credentials with that garage so they can sign in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Garage Name</Label>
                <Input required value={form.name} data-testid="garage-form-name"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Garage Two - Chennai" />
              </div>
              <div>
                <Label>Login Code</Label>
                <Input required value={form.garage_code} data-testid="garage-form-code"
                  onChange={(e) => setForm({ ...form, garage_code: e.target.value.toUpperCase() })} placeholder="GARAGE2" />
              </div>
              <div>
                <Label>Business Name (on invoices)</Label>
                <Input value={form.business_name} data-testid="garage-form-business-name"
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Garage" />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input value={form.gstin} data-testid="garage-form-gstin"
                  onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} data-testid="garage-form-phone"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.email} data-testid="garage-form-email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">First Admin Login</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admin Username</Label>
                  <Input required className="no-uppercase" value={form.admin_username} data-testid="garage-form-admin-username"
                    onChange={(e) => setForm({ ...form, admin_username: e.target.value })} />
                </div>
                <div>
                  <Label>Admin Password</Label>
                  <Input required type="password" value={form.admin_password} data-testid="garage-form-admin-password"
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />
                </div>
                <div>
                  <Label>Admin Full Name</Label>
                  <Input required value={form.admin_full_name} data-testid="garage-form-admin-fullname"
                    onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} />
                </div>
                <div>
                  <Label>Admin Email</Label>
                  <Input required type="email" value={form.admin_email} data-testid="garage-form-admin-email"
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="garage-form-submit">
                {submitting ? "Creating..." : "Create Garage"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
